/**
 * LorPickerDialog
 *
 * Presentational dialog for selecting a Letter of Representation.
 * Used in the standard (non-premises) LOR submission flow.
 */
import { Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface LorPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  hasPendingSelection: boolean;
  onContinue: () => void;
  children: React.ReactNode;
}

export const LorPickerDialog: React.FC<LorPickerDialogProps> = ({
  open,
  onOpenChange,
  isSubmitting,
  hasPendingSelection,
  onContinue,
  children,
}) => {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isSubmitting) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select a Letter of Representation</DialogTitle>
          <DialogDescription>
            Choose which generated document to attach for internal review.
            Next, we will save the delivery details the reviewer will use on
            the Review & Send step. Nothing will be sent externally yet.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[28rem] overflow-y-auto pr-1">
          {children}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={onContinue}
            disabled={!hasPendingSelection || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Preparing…
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
